'use client';

import { Identify } from '@amplitude/analytics-core';
import * as amplitude from '@amplitude/unified';
import { useEffect, useState } from 'react';

import { authClient } from '@/lib/auth';
import { env, isProduction } from '@/lib/env';
import { logger } from '@/lib/logger';

/**
 * Amplitude Analytics Component
 *
 * Initializes Amplitude analytics following the official Next.js integration guide.
 * - Disabled in development mode
 * - Automatically identifies users when authenticated
 * - Configures autocapture, session replay, and performance tracking
 *
 * @see https://amplitude.com/docs/sdks/frameworks/nextjs-installation-guide
 */
export function Amplitude() {
  const { data: session } = authClient.useSession();
  const [isInitialized, setIsInitialized] = useState(false);

  // Initialize Amplitude once
  useEffect(() => {
    // Skip initialization if not in Production
    if (!isProduction) {
      return;
    }

    // Skip if API key is not configured
    if (!env.NEXT_PUBLIC_AMPLITUDE_API_KEY) {
      logger.warn('Amplitude API key not configured');
      return;
    }

    // Skip if not in browser
    if (typeof window === 'undefined') {
      return;
    }

    // Initialize Amplitude with recommended settings
    const initializeAmplitude = async () => {
      try {
        await amplitude.initAll(env.NEXT_PUBLIC_AMPLITUDE_API_KEY!, {
          analytics: {
            autocapture: {
              sessions: true,
              pageViews: true,
              formInteractions: true,
              fileDownloads: true,
              elementInteractions: true,
            },
            logLevel: amplitude.Types.LogLevel.Warn,
          },
          sessionReplay: {
            sampleRate: 1, // Sample 100% of sessions in production
          },
        });

        console.log('Amplitude initialized successfully');
        setIsInitialized(true); // Mark as initialized
      } catch (error) {
        logger.captureException(error as Error, {
          tags: { feature: 'analytics', action: 'init' },
        });
      }
    };

    // initializeAmplitude();
  }, []);

  // Auto-identify user when session changes (ONLY after initialization)
  useEffect(() => {
    // Wait for initialization
    if (!isInitialized) {
      return;
    }

    if (session?.user) {
      const { id, email, name } = session.user;

      try {
        // Set user ID
        amplitude.setUserId(id);

        // Set user properties
        const identify = new Identify()
          .set('email', email)
          .set('name', name || 'Unknown');

        amplitude.identify(identify);

        console.log('User identified in Amplitude', { userId: id, email });
      } catch (error) {
        logger.captureException(error as Error, {
          context: { userId: id, email },
          tags: { feature: 'analytics', action: 'identify' },
        });
      }
    } else {
      // Clear user on logout
      try {
        amplitude.setUserId(undefined);
        amplitude.reset();
        console.log('User cleared from Amplitude');
      } catch (error) {
        logger.captureException(error as Error, {
          tags: { feature: 'analytics', action: 'clear' },
        });
      }
    }
  }, [session?.user, isInitialized]); // Depend on both session AND initialization

  // This component doesn't render anything
  return null;
}

export default Amplitude;
